/**
 * Generate full permission name with system prefix
 * @param {number} systemId - System ID (1-4)
 * @param {string} permissionName - Base permission name
 * @returns {string} Full permission name
 */
export const generatePermissionName = (systemId, permissionName) => {
  return !!systemId ? `${systemId}_${permissionName}` : permissionName;
};

/**
 * Hajj Management System - Attribute-Based Access Control (ABAC)
 *
 * This system implements a comprehensive permission system based on the pattern:
 * {system_id}_{permission_name}
 *
 * Systems:
 * - 1: MAKKAH (makkah)
 * - 2: MADINA (madina)
 * - 3: SEASON_HR (seasonal_hr)
 * - 4: USER_MANAGEMENT (user_management)
 */

// System definitions
export const SYSTEMS = {
  MAKKAH: { id: 1, code: "makkah", name: "Makkah Management" },
  MADINA: { id: 2, code: "madina", name: "Madina Management" },
  SEASON_HR: { id: 3, code: "seasonal_hr", name: "Seasonal HR" },
  USER_MANAGEMENT: { id: 4, code: "user_management", name: "User Management" },
};

/**
 * Extract system ID and permission name from full permission
 * @param {string} fullPermission - Full permission name (e.g., "1_list_users")
 * @returns {Object} {systemId, permissionName}
 */
export const parsePermissionName = (fullPermission) => {
  const parts = fullPermission.split("_");
  const systemId = parseInt(parts[0]);
  const permissionName = Number.isNaN(systemId)
    ? fullPermission
    : parts.slice(1).join("_");
  return { systemId: Number.isNaN(systemId) ? null : systemId, permissionName };
};

/**
 * Check if user has permission for a specific action on a resource
 * @param {Object} user - User object with roles and attributes
 * @param {number} systemId - System ID (1-4)
 * @param {string} permissionName - Permission name
 * @param {Object} resource - Resource being acted upon (optional)
 * @param {Object} context - Additional context data (optional)
 * @returns {boolean} Whether user has permission
 */
export const hasPermission = (
  permissionSet,
  systemId,
  permissionName,
  resource = null,
  context = {},
) => {
  if (!(permissionSet instanceof Set)) {
    return false;
  }

  return permissionSet.has(generatePermissionName(systemId, permissionName));
};

/**
 * Check if user has permission using full permission name
 * @param {Object} user - User object
 * @param {string} fullPermissionName - Full permission name (e.g., "1_list_users")
 * @param {Object} resource - Resource being acted upon (optional)
 * @param {Object} context - Additional context data (optional)
 * @returns {boolean} Whether user has permission
 */
export const hasPermissionByName = (
  permissionSet,
  fullPermissionName,
  resource = null,
  context = {},
) => {
  const { systemId, permissionName } = parsePermissionName(fullPermissionName);
  return hasPermission(
    permissionSet,
    systemId,
    permissionName,
    resource,
    context,
  );
};

// /**
//  * Get all permissions for a user in a specific system
//  * @param {Object} user - User object
//  * @param {number} systemId - System ID
//  * @returns {Array} Array of permission names the user has
//  */
// export const getUserPermissions = (user, systemId) => {
//   const permissions = [];

//   if (!user || !user.roles || !Array.isArray(user.roles)) {
//     return permissions;
//   }

//   for (const role of user.roles) {
//     const rolePermissions = ROLE_PERMISSIONS[role.id];
//     if (!rolePermissions) continue;

//     const systemPermissions = rolePermissions[systemId];
//     if (!systemPermissions) continue;

//     for (const [permissionName, permission] of Object.entries(
//       systemPermissions,
//     )) {
//       if (permission === true) {
//         permissions.push(generatePermissionName(systemId, permissionName));
//       }
//     }
//   }

//   return [...new Set(permissions)]; // Remove duplicates
// };

/**
 * Check if user has any of the specified permissions
 * @param {Object} user - User object
 * @param {Array} permissionNames - Array of full permission names
 * @param {Object} resource - Resource being acted upon (optional)
 * @param {Object} context - Additional context data (optional)
 * @returns {boolean} Whether user has any of the permissions
 */
export const hasAnyPermission = (
  permissionSet,
  permissionNames,
  resource = null,
  context = {},
) => {
  return permissionNames.some((permissionName) =>
    hasPermissionByName(permissionSet, permissionName, resource, context),
  );
};

/**
 * Check if user has all of the specified permissions
 * @param {Object} user - User object
 * @param {Array} permissionNames - Array of full permission names
 * @param {Object} resource - Resource being acted upon (optional)
 * @param {Object} context - Additional context data (optional)
 * @returns {boolean} Whether user has all of the permissions
 */
export const hasAllPermissions = (
  permissionSet,
  permissionNames,
  resource = null,
  context = {},
) => {
  return permissionNames.every((permissionName) =>
    hasPermissionByName(permissionSet, permissionName, resource, context),
  );
};

/**
 * Get user's role hierarchy level
 * @param {Object} user - User object
 * @returns {number} Role level (higher = more permissions)
 */
export const getUserRoleLevel = (user) => {
  const roleLevels = {
    super_admin: 4,
    admin: 3,
    moderator: 2,
    user: 1,
  };

  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return 0;
  }

  return Math.max(...user.roles.map((role) => roleLevels[role.name] || 0));
};

/**
 * Check if user can access a system
 * @param {Object} user - User object
 * @param {number} systemId - System ID
 * @returns {boolean} Whether user can access the system
 */
export const canAccessSystem = (permissionSet, systemId) => {
  return permissionSet?.has?.(systemId);
};

/**
 * Convert permissions array to Set for O(1) lookup
 * @param {Array} permissions - Array of permission objects from API
 * @returns {Set} Set of permission names
 */
export const createPermissionSet = (permissions, systemIds) => {
  if (!Array.isArray(permissions)) {
    console.warn("Invalid permissions array:", permissions);
    return new Set();
  }

  return new Set([
    ...systemIds,
    ...permissions.map((p) => generatePermissionName(p.system_id, p.name.replace(/_for_user_management|_for_makkah|_for_madinah|_for_seasonal_hr|_for_madina/g, ""))),
  ]);
};

export default {
  SYSTEMS,
  generatePermissionName,
  parsePermissionName,
  // ROLE_PERMISSIONS,
  hasPermission,
  hasPermissionByName,
  // getUserPermissions,
  hasAnyPermission,
  hasAllPermissions,
  getUserRoleLevel,
  canAccessSystem,
  createPermissionSet,
};
